import React, { useState } from "react";

function StarRating({ value, onChange, label }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <p className="text-gray-600 text-sm flex-1 leading-snug">{label}</p>
      <div className="flex gap-0.5 flex-shrink-0">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="cursor-pointer p-0.5 transition-transform hover:scale-110"
          >
            <svg
              width="19"
              height="19"
              viewBox="0 0 24 24"
              fill={star <= (hovered || value) ? "#F59E0B" : "none"}
              stroke={star <= (hovered || value) ? "#F59E0B" : "#D1D5DB"}
              strokeWidth="1.5"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}

function RadioGroup({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <p className="text-gray-600 text-sm flex-1 leading-snug">{label}</p>
      <div className="flex gap-4 flex-shrink-0">
        {["Yes", "No", "Not Sure"].map((opt) => (
          <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
            <div
              onClick={() => onChange(opt)}
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center cursor-pointer transition-colors ${
                value === opt ? "border-[#19BFB7]" : "border-gray-300"
              }`}
            >
              {value === opt && <div className="w-2 h-2 rounded-full bg-[#19BFB7]" />}
            </div>
            <span className="text-gray-600 text-sm">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

const initialForm = {
  company: "",
  interview_date: "",
  position: "",
  overall_rating: 0,
  professionalism_rating: 0,
  questions_rating: 0,
  clarity_rating: 0,
  fair_process: "Yes",
  apply_again: "Yes",
  comment: "",
  anonymous: true,
};

function CandidateFeedbackModal({ onClose, companyName, jobTitle }) {
  const [form, setForm] = useState({
    ...initialForm,
    company: companyName || "",
    position: jobTitle || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const isValid = form.company.trim() && form.interview_date && form.position.trim();

  const handleConfirm = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      setSubmitted(true);
      setTimeout(onClose, 1500);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[560px] max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <h2 className="text-gray-900 font-semibold text-base">Candidate Feedback</h2>
              <p className="text-gray-400 text-xs mt-0.5">Please enter the details below to add feedback</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer p-1 flex-shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto flex-1">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#19BFB7" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="text-gray-800 font-semibold">Feedback submitted!</p>
              <p className="text-gray-400 text-sm">Thank you for your response.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-gray-700 text-sm font-medium">
                    Company you interviewed with <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    placeholder="e.g. IBM"
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#19BFB7] transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-gray-700 text-sm font-medium">
                    Interview Date <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.interview_date}
                    onChange={(e) => set("interview_date", e.target.value)}
                    className="outline-none w-full text-gray-500 border border-gray-300 rounded-lg p-2 text-sm"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-gray-700 text-sm font-medium">
                    Position applied for <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.position}
                    onChange={(e) => set("position", e.target.value)}
                    placeholder="e.g. UI/UX Designer"
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#19BFB7] transition-colors"
                  />
                </div>
              </div>

              <div className="border border-gray-100 rounded-xl px-4 py-1">
                <StarRating
                  value={form.overall_rating}
                  onChange={(v) => set("overall_rating", v)}
                  label="Overall interview experience"
                />
                <StarRating
                  value={form.professionalism_rating}
                  onChange={(v) => set("professionalism_rating", v)}
                  label="Professionalism of interviewer(s)"
                />
                <StarRating
                  value={form.questions_rating}
                  onChange={(v) => set("questions_rating", v)}
                  label="Clarity and relevance of questions"
                />
                <StarRating
                  value={form.clarity_rating}
                  onChange={(v) => set("clarity_rating", v)}
                  label="Explanation of role, company & next steps"
                />
                <RadioGroup
                  label="Interview process was fair and unbiased"
                  value={form.fair_process}
                  onChange={(v) => set("fair_process", v)}
                />
                <RadioGroup
                  label="Would consider applying again in the future"
                  value={form.apply_again}
                  onChange={(v) => set("apply_again", v)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-gray-700 text-sm font-medium">Comment box</label>
                <textarea
                  value={form.comment}
                  onChange={(e) => set("comment", e.target.value)}
                  placeholder="Enter description"
                  rows={4}
                  className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 placeholder-gray-300 resize-none focus:outline-none focus:border-[#19BFB7] transition-colors"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => set("anonymous", !form.anonymous)}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 ${
                    form.anonymous ? "bg-[#19BFB7] border-[#19BFB7]" : "border-gray-300"
                  }`}
                >
                  {form.anonymous && (
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span className="text-gray-700 text-sm">Send anonymously.</span>
              </label>

              {error && <p className="text-red-500 text-sm">{error}</p>}
            </>
          )}
        </div>

        {!submitted && (
          <div className="px-6 pb-6 pt-4 flex gap-3 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={submitting || !isValid}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer ${
                isValid && !submitting
                  ? "bg-[#19BFB7] text-white hover:bg-[#17ADA6]"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {submitting ? "Submitting..." : "Confirm"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidateFeedbackModal;
